import dayjs from 'dayjs';
import { useMemo, useRef, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { useAuthUser } from 'auth/hooks';
import { ICliJob } from 'cli/types';
import { Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useCliJobHandler } from 'core/hooks';
import { FormProvider, useNotificationContext } from 'core/states';
import { CorporateLocationHelper } from 'corporate/helpers';
import { canOrderStillBeSubmitted } from 'quotes/components/CustomerQuotePartList/quoteStatus';
import { ShipSelection } from 'quotes/components/CustomerQuoteUpsert/ShipSelectionDateFields/enums';
import { OrderType, QuoteEntityType } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { ICustomerQuote, IProjectSubmitWarning } from 'quotes/types';
import { getDefaultEarliestAcceptableDate, processSubmitOrderErrorMessages } from 'quotes/utils';
import { Color } from 'theme/enums';

import { mapSubmitOrderForm } from './mappers';
import { submitCustomerOrderSchema } from './schema';
import { SeeExternalOrderButton } from './SeeExternalOrderButton';
import { SubmitCustomerOrderForm } from './SubmitCustomerOrderForm';
import { ISubmitOrderForm } from './types';
import { getSubmitOrderBlockReason, isCreditHoldConfirmationRequired } from './utils';

interface IProps {
  isOpen?: boolean;
  onClose?: () => void;
  quote: ICustomerQuote;
  // From the details query, not `quote`: save/submit responses replace the local quote without it.
  projectSubmitWarning?: IProjectSubmitWarning | null;
  onSubmitSuccess?: (updatedQuote?: ICustomerQuote) => void;
}

export const SubmitCustomerOrder = ({
  isOpen,
  onClose,
  quote,
  projectSubmitWarning = null,
  onSubmitSuccess,
}: IProps) => {
  const [displayEditShipToAddressButton, setDisplayEditShipToAddressButton] = useState(false);
  // Real in-flight lock. The LOADING banner used to be the only lock, and Escape/backdrop
  // cleared it while the CLI job kept polling — a second submit could then race the first.
  // The ref mirrors the state so a same-tick second call (Enter key) is rejected too. A lost
  // status check releases it only once the refetched order is in: its status and external order
  // number then block a re-submit, and the BE hands a re-submit the still-pending job.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isInFlightRef = useRef(false);
  const [pendingCreditHoldSubmit, setPendingCreditHoldSubmit] = useState<(() => void) | null>(null);

  const acquireLock = () => {
    isInFlightRef.current = true;
    setIsSubmitting(true);
  };

  const releaseLock = () => {
    isInFlightRef.current = false;
    setIsSubmitting(false);
  };

  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // A submit that ends in anything but success can have moved the order to Submission Timed Out
  // and written externalOrderErrorMessage itself — the BE job does that, not a webhook, so no SSE
  // event announces it. Refetch so the status chip and the submit lock catch up.
  const refreshQuoteStatus = () => queryClient.invalidateQueries('quote');

  const {
    clearDrawerNotification,
    clearFormNotification,
    showErrorNotification,
    showSuccessNotification,
  } = useNotificationContext();
  const { user } = useAuthUser();
  const { handleCliJob } = useCliJobHandler();

  const { submitCustomerOrder, isSubmitCustomerOrderLoading } = useQuoteActions();

  const isInFlight = isSubmitCustomerOrderLoading || isSubmitting;

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ quote });

  const isIntegrationEnabled = isAlternateVersionEnabled
    ? AlternateVersionHelper.isDdpOrderIntegrationEnabled({ user })
    : user.companySettings?.coreOrderIntegrationEnabled;

  const defaultValues = useMemo(
    () =>
      isIntegrationEnabled
        ? {
            externalUserId: user.externalUserId,
            location:
              quote.location?.id &&
              CorporateLocationHelper.isSales(quote.location, isAlternateVersionEnabled)
                ? {
                    ...quote.location,
                    vendorId: isAlternateVersionEnabled
                      ? quote.location.externalLocationId
                      : quote.location.vendorIdentifier,
                  }
                : null,
            ...(isAlternateVersionEnabled
              ? {
                  orderType: quote.orderType || OrderType.BILLABLE,
                  shipSelection: quote.shipSelection ?? ShipSelection.NO_DELIVERY_DATE,
                  earliestAcceptableDate: getDefaultEarliestAcceptableDate(quote),
                  requiredOnSiteDate: quote.requiredOnSiteDate
                    ? dayjs(quote.requiredOnSiteDate)
                    : null,
                  shippingMethod: quote.shippingMethodId
                    ? {
                        id: quote.shippingMethodId,
                        name: quote.shippingMethodName,
                      }
                    : null,
                  freightTerm: quote.freightTermId
                    ? {
                        id: quote.freightTermId,
                        name: quote.freightTermName,
                      }
                    : null,
                }
              : {}),
          }
        : {},
    [
      isAlternateVersionEnabled,
      isIntegrationEnabled,
      quote.alternateVersionEnabled,
      quote.earliestAcceptableDate,
      quote.freightTermId,
      quote.isOrder,
      quote.freightTermName,
      quote.location,
      quote.orderType,
      quote.requiredOnSiteDate,
      quote.shipSelection,
      quote.shippingMethodId,
      quote.shippingMethodName,
      quote.status,
      user.externalUserId,
    ]
  );

  const notificationPosition = isIntegrationEnabled
    ? NotificationPosition.FORM
    : NotificationPosition.TOP;

  const onSubmit = (reset?: UseFormReset<ISubmitOrderForm>) => async (data?: ISubmitOrderForm) => {
    // Handler-level guard: the disabled Submit button is not the only way in (Enter key on a
    // field, the non-integration Confirmation), and the quote may have gained an external
    // order number via SSE since the drawer opened.
    if (isInFlightRef.current || getSubmitOrderBlockReason(quote)) {
      return;
    }

    if (!isIntegrationEnabled) {
      onClose();
    }

    acquireLock();

    const started = await handleCliJob(
      submitCustomerOrder,
      mapSubmitOrderForm(data, quote.id, {
        isCompanyAdmin: user.companyAdmin,
        isAlternateVersionEnabled,
      }),
      {
        cliJobInProgressMessage: t('messageOrderSubmitInProgress'),
        onCliJobFinished: (cliJob: ICliJob) => {
          releaseLock();

          const order = cliJob.result as ICustomerQuote;

          handleOnSubmitSuccess(reset, order);
        },
        onCliJobStopped: (cliJob: ICliJob) => {
          releaseLock();

          const result = cliJob.result as Record<string, unknown>;

          const { title, message, displayEditShipToAddressButton } =
            processSubmitOrderErrorMessages({
              ...result,
              error: cliJob?.statusDescription,
            });

          setDisplayEditShipToAddressButton(displayEditShipToAddressButton);

          showErrorNotification(
            {
              title: title || t('errorTitleQuoteSubmitOrder'),
              message,
              disableAutoClose: true,
            },
            notificationPosition
          );

          refreshQuoteStatus();
        },
        onCliJobPollFailed: async ({ showLostTrackNotice }) => {
          await Promise.all([
            refreshQuoteStatus(),
            queryClient.invalidateQueries('customerQuoteParts'),
          ]);

          releaseLock();
          showLostTrackNotice();
        },
        notificationPosition,
      }
    );

    // The enqueue POST failed, but the job row can already exist server-side (a dropped response
    // on a gateway timeout), so the order may have moved on without us.
    if (!started) {
      releaseLock();
      refreshQuoteStatus();
    }
  };

  const activeProjectSubmitWarning = canOrderStillBeSubmitted(QuoteEntityType.ORDER, quote)
    ? projectSubmitWarning
    : null;

  const requestSubmit =
    (reset?: UseFormReset<ISubmitOrderForm>) => async (data?: ISubmitOrderForm) => {
      if (isInFlightRef.current || getSubmitOrderBlockReason(quote)) {
        return;
      }

      if (isCreditHoldConfirmationRequired(activeProjectSubmitWarning)) {
        setPendingCreditHoldSubmit(() => () => onSubmit(reset)(data));

        return;
      }

      await onSubmit(reset)(data);
    };

  const onConfirmCreditHold = () => {
    const submit = pendingCreditHoldSubmit;

    setPendingCreditHoldSubmit(null);
    submit?.();
  };

  const creditHoldConfirmation = (
    <Confirmation
      isOpen={!!pendingCreditHoldSubmit}
      title={t('titleCreditHoldConfirmation')}
      message={t('messageCreditHoldConfirmation', {
        projectName: activeProjectSubmitWarning?.projectName,
      })}
      color={Color.WARNING}
      confirmBtnColor={Color.WARNING}
      onConfirm={onConfirmCreditHold}
      onCancel={() => setPendingCreditHoldSubmit(null)}
    />
  );

  const onSubmitSuccessEditShipToAddress = () => setDisplayEditShipToAddressButton(false);

  const closeAndReset = (reset?: UseFormReset<ISubmitOrderForm>) => {
    setDisplayEditShipToAddressButton(false);
    clearDrawerNotification();
    // The integrated submit's "Submitting order." banner sits at FORM position and no success
    // banner replaces it any more; left in place it would keep the next form's buttons disabled.
    clearFormNotification();

    reset && reset(defaultValues);
    onClose();
  };

  // User-initiated close: ignored while a submit is in flight (the drawer also blocks
  // Escape/backdrop via disableAutoClose); the success path closes through closeAndReset.
  const handleOnClose = (reset?: UseFormReset<ISubmitOrderForm>) => {
    if (isInFlight) {
      return;
    }

    closeAndReset(reset);
  };

  const handleOnSubmitSuccess = (reset: UseFormReset<ISubmitOrderForm>, order: ICustomerQuote) => {
    onSubmitSuccess?.(order);

    queryClient.invalidateQueries('customerQuoteParts');

    closeAndReset(reset);

    // A DDP (alternate version) submit is announced by the notification card the BE writes; a
    // banner here would be a second popup for the same event. The legacy core integration and the
    // non-integrated path write no card, so they keep the banner.
    if (isIntegrationEnabled && isAlternateVersionEnabled) {
      return;
    }

    showSuccessNotification({ message: t('messageOrderSubmitSuccess') }, NotificationPosition.TOP);

    if (isAlternateVersionEnabled) {
      const hasExternalOrderNumber = !!order.externalOrderNumber;

      showSuccessNotification(
        {
          message: hasExternalOrderNumber
            ? t('messageOrderSubmitSuccessExternalWithExternalOrderNumber', {
                externalOrderNumber: order.externalOrderNumber,
              })
            : t('messageOrderSubmitSuccessExternal'),
          buttons: hasExternalOrderNumber
            ? [<SeeExternalOrderButton key="seeExternalOrderButton" quote={order} />]
            : undefined,
          disableAutoClose: true,
        },
        NotificationPosition.TOP
      );
    }
  };

  if (!isIntegrationEnabled) {
    return (
      <>
        <Confirmation
          isOpen={isOpen && !pendingCreditHoldSubmit}
          title={t('titleSubmitOrder')}
          message={t('messageConfirmSubmitOrder')}
          confirmBtnColor={Color.SUCCESS}
          onConfirm={() => requestSubmit()()}
          onCancel={onClose}
        />

        {creditHoldConfirmation}
      </>
    );
  }

  return (
    <>
      <FormProvider
        schema={submitCustomerOrderSchema(user, isAlternateVersionEnabled)}
        defaultValues={defaultValues}
        resetDefaultValues
      >
        <SubmitCustomerOrderForm
          quote={quote}
          projectSubmitWarning={activeProjectSubmitWarning}
          isOpen={isOpen}
          onClose={handleOnClose}
          onSubmit={requestSubmit}
          // Disabled for the whole submit: the initial mutation AND while the CLI job polls. The
          // "Submitting order." banner is a visual signal only — it is user-dismissible and
          // carries no locking guarantee.
          isSubmitOrderLoading={isInFlight}
          disableAutoClose={isInFlight}
          displayEditShipToAddressButton={displayEditShipToAddressButton}
          onSubmitSuccessEditShipToAddress={onSubmitSuccessEditShipToAddress}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
        />
      </FormProvider>

      {creditHoldConfirmation}
    </>
  );
};
